import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Shield, RefreshCw, Zap, Lock, ArrowLeft } from 'lucide-react';
import { apiPost } from '../../lib/api.js';
import { useAuth } from '../../store/auth.js';
import toast from 'react-hot-toast';
import DepositAmountPicker from './DepositAmountPicker.jsx';
import TrialDepositBanner from './TrialDepositBanner.jsx';
import { DEFAULT_RECHARGE, money, parseCustomAmount, trialEnd } from '../../lib/walletDeposit.js';

const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
// Created once at module scope so Elements never re-initializes Stripe.js.
const stripePromise = publishableKey ? loadStripe(publishableKey) : null;

const elementsAppearance = {
  theme: 'night',
  variables: { colorPrimary: '#8b5cf6', colorBackground: '#140b22', borderRadius: '8px' },
};

// Rendered inside <Elements>: collects the card with Stripe's Payment Element and
// confirms the deposit PaymentIntent. Card data never touches our servers.
function PaymentStep({ amountUsd, paymentIntentId, onPaid, onBack }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  async function handlePay(e) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: { return_url: `${window.location.origin}/phone-setup` },
      });
      if (error) throw new Error(error.message || 'Payment failed');
      if (paymentIntent?.status !== 'succeeded') {
        throw new Error(`Payment not completed (${paymentIntent?.status || 'unknown'})`);
      }
      const res = await apiPost('/stripe/wallet-initial-deposit/confirm', { paymentIntentId });
      if (!res?.ok) throw new Error(res?.error || 'Could not credit your wallet');
      onPaid(res);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Payment processing failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handlePay} className="space-y-5">
      <div className="p-4 rounded-xl border border-white/10 bg-white/5">
        {/* Card only: no ACH / Pay by Bank / Cash App, no Link promotional banners or cash-back. */}
        <PaymentElement
          options={{
            layout: 'tabs',
            paymentMethodOrder: ['card'],
            wallets: {
              link: 'never',
              applePay: 'auto',
              googlePay: 'auto',
            },
            terms: { card: 'never' },
          }}
        />
      </div>
      <p className="text-[11px] text-white/40">
        Sandbox testing: use card 4242 4242 4242 4242, any future expiry, any CVC.
      </p>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          disabled={submitting}
          className="px-4 py-3 rounded-lg border border-white/15 text-white/70 hover:text-white hover:border-white/30 transition flex items-center gap-1.5 text-sm disabled:opacity-50"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          type="submit"
          disabled={!stripe || submitting}
          className="btn-primary flex-1 py-3 text-base font-semibold flex items-center justify-center gap-2 shadow-glow-purple disabled:opacity-50"
        >
          {submitting ? (
            <><RefreshCw className="w-4 h-4 animate-spin" /> Processing Deposit…</>
          ) : (
            <><Lock className="w-4 h-4" /> Deposit ${money(amountUsd)} & Continue to Line Setup</>
          )}
        </button>
      </div>
    </form>
  );
}

export default function AgentBillingSetup({ onComplete }) {
  const { agent, token, setSession } = useAuth();
  const [selectedTier, setSelectedTier] = useState(25); // preset amount or 'custom'
  const [customAmount, setCustomAmount] = useState('');
  const [autoRecharge, setAutoRecharge] = useState(true);
  const [intent, setIntent] = useState(null); // { clientSecret, paymentIntentId, amountUsd }
  const [starting, setStarting] = useState(false);

  // The chosen preset or the validated custom amount drives the payload, the
  // button label and the auto-recharge copy.
  const custom = parseCustomAmount(customAmount);
  const isCustom = selectedTier === 'custom';
  const amountUsd = isCustom ? custom.amount : selectedTier;
  const rechargeUsd = isCustom && custom.amount ? Math.round(custom.amount) : DEFAULT_RECHARGE;
  const amountError = isCustom ? (customAmount ? custom.error : '') : '';
  const trialEndDate = trialEnd(agent);

  // Credit the local session so gated screens (PhoneSetup `needsWallet`) flip
  // instantly — no refresh needed.
  function applyFunded(res, amount) {
    toast.success(`Wallet funded with $${money(amount)}!`, { icon: '💳' });
    setSession(token, {
      ...(agent || {}),
      ...(res.agent || {}),
      walletBalance: res.walletBalance,
      autoRecharge: res.autoRecharge,
      rechargeAmount: res.rechargeAmount,
      hasPaymentMethodOnFile: true,
    });
    if (onComplete) onComplete(res);
  }

  async function startPayment() {
    if (!amountUsd) {
      toast.error(custom.error || 'Enter a deposit amount of at least $10.00');
      return;
    }
    setStarting(true);
    try {
      const res = await apiPost('/stripe/wallet-initial-deposit', {
        amountUsd,
        autoRecharge,
        rechargeAmountUsd: rechargeUsd,
      });
      if (!res?.ok || !res.clientSecret) throw new Error(res?.error || 'Could not start payment');
      setIntent(res);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Could not start payment');
    } finally {
      setStarting(false);
    }
  }

  // Dev/QA shortcut (non-production builds only; the server also refuses it in
  // production): skips Stripe and credits a simulated $10.
  async function handleQuickFund() {
    setStarting(true);
    try {
      const res = await apiPost('/billing/setup-wallet', { amount: 10, stub: true });
      if (!res?.ok) throw new Error(res?.error || 'Failed to initialize wallet');
      applyFunded(res, 10);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Quick fund failed');
    } finally {
      setStarting(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6">
      <div className="panel p-6 sm:p-8 space-y-6 border border-civix-purple/30 bg-gradient-to-b from-civix-panel to-black/90 shadow-2xl rounded-2xl relative">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-civix-purple/20 text-civix-cyan border border-civix-purple/30">
            <Zap className="w-3.5 h-3.5" /> Self-Funded Agent Wallet
          </div>
          <h2 className="font-display text-2xl font-bold text-white tracking-tight">
            Telecom Wallet Funding
          </h2>
        </div>

        <TrialDepositBanner trialEndDate={trialEndDate} amountUsd={amountUsd} />

        {!intent ? (
          <div className="space-y-6">
            <DepositAmountPicker
              selected={selectedTier}
              onSelect={setSelectedTier}
              customAmount={customAmount}
              onCustomChange={setCustomAmount}
              error={amountError}
            />

            <div className="p-4 rounded-xl border border-white/10 bg-black/40">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={autoRecharge}
                  onChange={(e) => setAutoRecharge(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-white/20 bg-white/5 text-civix-purple focus:ring-civix-purple accent-civix-purple cursor-pointer"
                />
                <div className="space-y-1">
                  <div className="text-sm font-semibold text-white">
                    Auto-recharge with ${money(rechargeUsd)} when balance falls below $5.00
                  </div>
                  <p className="text-xs text-white/50 leading-relaxed">
                    Prevents dropped dials or failed SMS when your carrier balance runs low.
                  </p>
                </div>
              </label>
            </div>

            {!stripePromise && (
              <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/10 text-xs text-amber-200">
                Stripe is not configured (VITE_STRIPE_PUBLISHABLE_KEY is missing) — card payments are unavailable.
              </div>
            )}

            <button
              type="button"
              onClick={startPayment}
              disabled={starting || !stripePromise || !amountUsd}
              className="btn-primary w-full py-3 text-base font-semibold flex items-center justify-center gap-2 shadow-glow-purple disabled:opacity-50"
            >
              {starting ? (
                <><RefreshCw className="w-4 h-4 animate-spin" /> Preparing secure checkout…</>
              ) : (
                <><Lock className="w-4 h-4" /> Continue to Secure Payment{amountUsd ? ` — $${money(amountUsd)}` : ''}</>
              )}
            </button>

            {import.meta.env.DEV && (
              <button
                type="button"
                onClick={handleQuickFund}
                disabled={starting}
                className="w-full py-2.5 rounded-lg text-sm font-semibold border border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20 transition disabled:opacity-50"
              >
                ⚡ Dev Mode: Quick Fund ($10 Mock Credit, no card)
              </button>
            )}
          </div>
        ) : (
          <Elements
            stripe={stripePromise}
            options={{ clientSecret: intent.clientSecret, appearance: elementsAppearance }}
          >
            <PaymentStep
              amountUsd={intent.amountUsd}
              paymentIntentId={intent.paymentIntentId}
              onPaid={(res) => applyFunded(res, intent.amountUsd)}
              onBack={() => setIntent(null)}
            />
          </Elements>
        )}

        <div className="flex items-center justify-center gap-2 text-xs text-white/40 text-center">
          <Shield className="w-3.5 h-3.5 text-civix-cyan" />
          <span>Card details are entered in Stripe's secure form and never touch CIVIX servers.</span>
        </div>
      </div>
    </div>
  );
}
